import { useMemo } from 'react';
import { parseCurrencyToNumber, formatCurrency } from '../services/RevenueCalculationService';
import { calculatePreviousYearRevenueForYear, filterClientsByRetention } from '../utils/yearSpecificUtils';
import { useAppContext } from '../context/AppContext';
import { getClientKey } from '../utils/clientUtils';
import { getConferenceMatchingClients, filterClientsByConference } from '../services/conferenceFilterService';
import { enrichClientsWithConferenceRevenue, enrichClientsWithConferenceRooms } from '../services/conferenceRevenueService';
import { YEAR_CONFIG } from '../config/yearConfig';

/**
 * Resolve which year should be used for revenue sorting.
 * @param {string} selectedYear
 * @returns {string}
 */
export const getRevenueSortYear = (selectedYear) => {
  return selectedYear === 'All'
    ? String(YEAR_CONFIG.lastDataYear)
    : selectedYear;
};

// Field type mapping for sorting
const FIELD_TYPE_MAP = {
  revenue: 'number',
  last_booking_revenue: 'number',
  group_size: 'number',
  tier: 'number',
  // Add more numeric fields as needed
  // Example: '2024 Rooms': 'number', '2024 RN': 'number'
};

/**
 * @param {Array} pinnedClients
 * @param {string} pinnedSearchQuery
 * @param {Set} activeTierFilters
 * @param {object} sortConfig
 * @param {Set} lockedClients
 * @param {object} options - { retentionFilter: 'default'|'lost'|'retained' }
 */
const usePinnedListFilters = (pinnedClients, pinnedSearchQuery, activeTierFilters, sortConfig, lockedClients, options = {}) => {
  // Get client details for display - Updated for new data structure
  const getClientDetails = (client) => {
    return {
      revenue: client['Total Revenue'] || '$0.00',
      lastBookingRevenue: client['Last Booking Revenue'] || 'N/A',
      lastBooked: client['Last Booked'] || 'N/A',
      contactName: client['Key Contact Name'] || 'Contact Representative',
      contactEmail: client['Key Contact Email'] || 'contact@client.com',
      previousManager: client['Previously Assigned Manager'] || 'None',
      
      // New fields from portfolio data
      timeZone: client['Time zone'] || 'N/A',
      tier: client['Tier'] || 'N/A',
      segment: client['Segment'] || 'N/A',
      rn: client['RN'] || 'N/A'
    };
  };

  // Helper function to check if client is locked
  const isClientLocked = (client) => {
    return lockedClients.has(getClientKey(client));
  };

  // Sort and filter clients based on current sort configuration and search
  const { clientData, filters: { selectedYear }, selectedConference, sponsorData } = useAppContext();

  // Compute available years (sorted ascending)
  const availableYears = useMemo(() => {
    const yearsSet = new Set();
    clientData.forEach(client => {
      Object.keys(client).forEach(key => {
        const match = key.match(/^(\d{4}) Revenue$/);
        if (match) yearsSet.add(match[1]);
      });
    });
    return Array.from(yearsSet).sort();
  }, [clientData]);

  const sortedClients = useMemo(() => {
    let filteredClients = [...pinnedClients];

    // Apply conference filter first (if selected)
    if (selectedConference && selectedConference !== 'All') {
      const matchingCustomers = getConferenceMatchingClients(
        sponsorData,
        selectedConference,
        selectedYear
      );
      filteredClients = filterClientsByConference(filteredClients, matchingCustomers);
    }

    // Enrich clients with conference revenue (must be done after conference filtering)
    filteredClients = enrichClientsWithConferenceRevenue(
      filteredClients,
      selectedConference,
      selectedYear,
      sponsorData
    );

    // Enrich clients with conference rooms (must be done after conference filtering)
    filteredClients = enrichClientsWithConferenceRooms(
      filteredClients,
      selectedConference,
      selectedYear,
      sponsorData
    );

    // Retention filter (only if not "All" years and retentionFilter is set)
    const retentionFilter = options.retentionFilter || 'default';
    if (selectedYear !== 'All' && availableYears.length > 0 && retentionFilter !== 'default') {
      filteredClients = filterClientsByRetention(filteredClients, selectedYear, availableYears, retentionFilter);
    }

    // Apply tier filter first
    if (activeTierFilters.size > 0) {
      filteredClients = filteredClients.filter(client => {
        const clientTier = client['Tier'] || 'Unknown';
        return activeTierFilters.has(clientTier);
      });
    }

    // Apply universal search filter
    if (pinnedSearchQuery.trim()) {
      const query = pinnedSearchQuery.toLowerCase().trim();
      filteredClients = filteredClients.filter(client => {
        const details = getClientDetails(client);

        // Search through all relevant fields
        const searchableFields = [
          client.client_name,
          client.country,
          client.conference_manager,
          client.group_size?.toString(),
          details.tier,
          details.revenue,
          details.lastBookingRevenue,
          details.lastBooked,
          details.contactName,
          details.contactEmail,
          details.timeZone,
          details.segment,
          details.rn,
          details.previousManager
        ];

        return searchableFields.some(field =>
          field && field.toString().toLowerCase().includes(query)
        );
      });
    }

    // Separate locked clients from unlocked clients
    const lockedClientsList = [];
    const unlockedClients = [];

    filteredClients.forEach(client => {
      if (isClientLocked(client)) {
        lockedClientsList.push(client);
      } else {
        unlockedClients.push(client);
      }
    });

    // Apply sorting to ALL unlocked clients (both external and original)
    if (sortConfig.key !== null) {
      unlockedClients.sort((a, b) => {
        // Handle special cases for revenue fields
        if (sortConfig.key === 'revenue') {
          // Use conference revenue if active, otherwise use year-specific revenue
          let aValue, bValue;
          if (a.isConferenceRevenueActive && typeof a.conferenceRevenue === 'number') {
            aValue = a.conferenceRevenue;
          } else {
            // Determine which year's revenue to use
            const revenueYear = getRevenueSortYear(selectedYear);
            const revenueColumn = `${revenueYear} Revenue`;
            const revenueValue = a[revenueColumn];
            aValue = parseCurrencyToNumber(revenueValue);
          }
          
          if (b.isConferenceRevenueActive && typeof b.conferenceRevenue === 'number') {
            bValue = b.conferenceRevenue;
          } else {
            // Determine which year's revenue to use
            const revenueYear = getRevenueSortYear(selectedYear);
            const revenueColumn = `${revenueYear} Revenue`;
            const revenueValue = b[revenueColumn];
            bValue = parseCurrencyToNumber(revenueValue);
          }
          
          // Primary sort by revenue
          const revenueDiff = sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
          
          // Secondary sort by client name for equal revenue (stable sort)
          if (revenueDiff === 0) {
            const aName = (a.client_name || '').toLowerCase();
            const bName = (b.client_name || '').toLowerCase();
            return aName.localeCompare(bName);
          }
          
          return revenueDiff;
        }

        // Handle special case for last booking revenue
        if (sortConfig.key === 'last_booking_revenue') {
          const aDetails = getClientDetails(a);
          const bDetails = getClientDetails(b);
          const aValue = parseCurrencyToNumber(aDetails.lastBookingRevenue);
          const bValue = parseCurrencyToNumber(bDetails.lastBookingRevenue);
          return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
        }

        // Handle special case for contact name
        if (sortConfig.key === 'contact') {
          const aDetails = getClientDetails(a);
          const bDetails = getClientDetails(b);
          const aValue = aDetails.contactName.toLowerCase();
          const bValue = bDetails.contactName.toLowerCase();
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        // Handle tier sorting
        if (sortConfig.key === 'tier') {
          const aDetails = getClientDetails(a);
          const bDetails = getClientDetails(b);
          const aValue = parseInt(aDetails.tier) || 0;
          const bValue = parseInt(bDetails.tier) || 0;
          return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
        }

        // Special case: sort "group_size" (Total Rooms) by conference or year-specific value
        if (sortConfig.key === 'group_size') {
          let aRooms, bRooms;
          
          // Use conference rooms if active, otherwise use year-specific rooms
          if (a.isConferenceRoomsActive && typeof a.conferenceRooms === 'number') {
            aRooms = a.conferenceRooms;
          } else {
            const { getYearSpecificData } = require('../utils/yearSpecificUtils');
            aRooms = parseFloat(getYearSpecificData(a, selectedYear).rooms) || 0;
          }
          
          if (b.isConferenceRoomsActive && typeof b.conferenceRooms === 'number') {
            bRooms = b.conferenceRooms;
          } else {
            const { getYearSpecificData } = require('../utils/yearSpecificUtils');
            bRooms = parseFloat(getYearSpecificData(b, selectedYear).rooms) || 0;
          }
          
          const diff = sortConfig.direction === 'ascending' ? aRooms - bRooms : bRooms - aRooms;
          if (diff === 0) {
            const aName = (a.client_name || '').toLowerCase();
            const bName = (b.client_name || '').toLowerCase();
            return aName.localeCompare(bName);
          }
          return diff;
        }
        // Modular numeric sorting for mapped fields
        if (FIELD_TYPE_MAP[sortConfig.key] === 'number') {
          // Try to parse as float, fallback to 0 if missing/invalid
          const aValue = parseFloat(a[sortConfig.key]) || 0;
          const bValue = parseFloat(b[sortConfig.key]) || 0;
          const diff = sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
          // Secondary sort by client name for stable ordering
          if (diff === 0) {
            const aName = (a.client_name || '').toLowerCase();
            const bName = (b.client_name || '').toLowerCase();
            return aName.localeCompare(bName);
          }
          return diff;
        }

        // Try to auto-detect numeric fields (e.g., year columns like "2024 Rooms")
        if (
          typeof a[sortConfig.key] !== 'undefined' &&
          typeof b[sortConfig.key] !== 'undefined' &&
          !isNaN(parseFloat(a[sortConfig.key])) &&
          !isNaN(parseFloat(b[sortConfig.key]))
        ) {
          const aValue = parseFloat(a[sortConfig.key]);
          const bValue = parseFloat(b[sortConfig.key]);
          return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
        }

        // Default: string comparison, handle null/undefined
        const aValue = (a[sortConfig.key] ?? '').toString().toLowerCase();
        const bValue = (b[sortConfig.key] ?? '').toString().toLowerCase();
        return sortConfig.direction === 'ascending'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    }

    // Put locked clients at the top, then sorted unlocked clients
    return [...lockedClientsList, ...unlockedClients];
  }, [pinnedClients, sortConfig, pinnedSearchQuery, activeTierFilters, lockedClients, selectedYear, availableYears, options.retentionFilter, selectedConference, sponsorData]);

  // Calculate revenue for filtered clients using centralized service
  const calculateFilteredRevenue = (clients) => {
    const displayYear = getRevenueSortYear(selectedYear);

    if (clients.length === 0) {
      // Check if conference is active
      if (selectedConference && selectedConference !== 'All') {
        const label = selectedYear === 'All' 
          ? `${selectedConference} All Years` 
          : `${selectedConference} ${displayYear}`;
        return { yearSpecific: `${label}: $0` };
      }
      
      return { yearSpecific: `${displayYear}: $0` };
    }

    // Check if clients have conference revenue active
    const hasConferenceRevenue = clients.some(c => c.isConferenceRevenueActive);

    if (hasConferenceRevenue) {
      // Use conference revenue
      let totalRevenue = 0;
      clients.forEach(client => {
        if (client.conferenceRevenue && typeof client.conferenceRevenue === 'number') {
          totalRevenue += client.conferenceRevenue;
        }
      });

      const label = selectedYear === 'All' 
        ? `${selectedConference} All Years` 
        : `${selectedConference} ${selectedYear}`;
      
      return {
        yearSpecific: `${label}: ${formatCurrency(totalRevenue, { compact: true })}`
      };
    }

    // Regular revenue calculation (no conference selected)
    let yearRevenue = 0;

    clients.forEach(client => {
      // Get revenue for the specific year
      const revenueColumn = `${displayYear} Revenue`;
      const revenueValue = client[revenueColumn];
      
      if (revenueValue) {
        yearRevenue += parseCurrencyToNumber(revenueValue);
      }
    });

    return {
      yearSpecific: `${displayYear}: ${formatCurrency(yearRevenue, { compact: true })}`
    };
  };

  return {
    sortedClients,
    getClientDetails,
    calculateFilteredRevenue
  };
};

export default usePinnedListFilters;
